import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, LogOut, Users, Sparkles, ClipboardList, BadgeCheck } from "lucide-react";
import api, { clearToken, getToken, formatApiError } from "@/lib/api";

export const STATUS_COLORS = {
  "New Applicant": "bg-blush-100 text-charcoal border-blush-300",
  "Reviewing": "bg-gold-soft text-gold-deep border-gold/40",
  "Interview Requested": "bg-blue-50 text-blue-700 border-blue-200",
  "Interview Scheduled": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Documents Needed": "bg-amber-50 text-amber-700 border-amber-200",
  "Approved for Hire": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Onboarding": "bg-teal-50 text-teal-700 border-teal-200",
  "Hired": "bg-green-100 text-green-800 border-green-300",
  "Incomplete": "bg-orange-50 text-orange-700 border-orange-200",
  "Not Selected": "bg-gray-100 text-gray-500 border-gray-200",
};

export const StatusBadge = ({ status }) => (
  <span className={`inline-flex whitespace-nowrap rounded-full border px-3 py-1 text-xs font-semibold ${STATUS_COLORS[status] || STATUS_COLORS["New Applicant"]}`}>
    {status}
  </span>
);

export const PortalHeader = ({ title, onLogout }) => (
  <header className="sticky top-0 z-40 border-b border-blush-200 bg-white/90 backdrop-blur-xl">
    <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
      <div className="flex items-center gap-3">
        <Link to="/"><img src="/logo-icon.png" alt="New Beginnings" className="h-9 w-9" /></Link>
        <span className="font-serif text-lg text-charcoal">{title}</span>
      </div>
      <button onClick={onLogout} data-testid="portal-logout-button"
        className="flex items-center gap-2 rounded-full border border-blush-300 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-charcoal-muted transition-colors hover:border-gold/50 hover:text-gold-deep">
        <LogOut className="h-3.5 w-3.5" /> Log out
      </button>
    </div>
  </header>
);

const AVAILABILITY_FILTERS = ["Full-time", "Part-time", "Per diem", "Weekdays", "Weekends", "Days", "Evenings", "Overnight", "Live-in"];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [apps, setApps] = useState([]);
  const [meta, setMeta] = useState({ statuses: [], positions: [] });
  const [filters, setFilters] = useState({ search: "", position: "", status: "", availability: "", location: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) return navigate("/admin");
    api.get("/auth/me")
      .then(({ data }) => {
        if (data.user.role !== "admin") throw new Error();
        setUser(data.user);
      })
      .catch(() => { clearToken(); navigate("/admin"); });
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.get("/admin/applications", { params });
      setApps(data.applications);
      setMeta({ statuses: data.statuses, positions: data.positions });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearToken();
    navigate("/admin");
  };

  const stats = {
    total: apps.length,
    fresh: apps.filter((a) => a.status === "New Applicant").length,
    pipeline: apps.filter((a) => !["Hired", "Not Selected", "New Applicant"].includes(a.status)).length,
    hired: apps.filter((a) => a.status === "Hired").length,
  };

  return (
    <div className="min-h-screen bg-blush-50/50">
      <PortalHeader title="Admin Dashboard" onLogout={logout} />
      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <h1 className="font-serif text-3xl text-charcoal" data-testid="admin-dashboard-heading">Employment Applications</h1>
        <p className="mt-1 text-sm text-charcoal-muted">Review, filter, and manage every applicant in one place.</p>

        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            { icon: Users, label: "Total", value: stats.total, testid: "admin-stat-total" },
            { icon: Sparkles, label: "New", value: stats.fresh, testid: "admin-stat-new" },
            { icon: ClipboardList, label: "In Pipeline", value: stats.pipeline, testid: "admin-stat-pipeline" },
            { icon: BadgeCheck, label: "Hired", value: stats.hired, testid: "admin-stat-hired" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid} className="rounded-2xl border border-blush-200 bg-white p-5">
              <s.icon className="h-5 w-5 text-gold" strokeWidth={1.5} />
              <p className="mt-3 font-serif text-3xl text-charcoal">{s.value}</p>
              <p className="text-xs uppercase tracking-[0.14em] text-charcoal-faint">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 grid gap-3 rounded-2xl border border-blush-200 bg-white p-5 sm:grid-cols-2 lg:grid-cols-5">
          <div className="relative lg:col-span-1">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-charcoal-faint" />
            <input data-testid="admin-search-input" placeholder="Search name, email, phone"
              value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="field-input !pl-11" />
          </div>
          <select data-testid="admin-filter-position" value={filters.position} onChange={(e) => setFilters({ ...filters, position: e.target.value })} className="field-input">
            <option value="">All positions</option>
            {meta.positions.map((p) => <option key={p}>{p}</option>)}
          </select>
          <select data-testid="admin-filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="field-input">
            <option value="">All statuses</option>
            {meta.statuses.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select data-testid="admin-filter-availability" value={filters.availability} onChange={(e) => setFilters({ ...filters, availability: e.target.value })} className="field-input">
            <option value="">Any availability</option>
            {AVAILABILITY_FILTERS.map((a) => <option key={a}>{a}</option>)}
          </select>
          <input data-testid="admin-filter-location" placeholder="Location / area"
            value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} className="field-input" />
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-blush-200 bg-white" data-testid="admin-applications-table">
          {loading ? (
            <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2 border-blush-300 border-t-gold" /></div>
          ) : apps.length === 0 ? (
            <p className="py-16 text-center text-sm text-charcoal-muted">No applications match your filters yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead>
                  <tr className="border-b border-gold/25 bg-blush-100/60 text-xs uppercase tracking-[0.12em] text-charcoal-muted">
                    <th className="px-6 py-4 font-semibold">Applicant</th>
                    <th className="px-4 py-4 font-semibold">Position(s)</th>
                    <th className="px-4 py-4 font-semibold">Availability</th>
                    <th className="px-4 py-4 font-semibold">Area</th>
                    <th className="px-4 py-4 font-semibold">Applied</th>
                    <th className="px-4 py-4 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {apps.map((a) => (
                    <tr key={a.id} data-testid={`admin-applicant-row-${a.id}`}
                      onClick={() => navigate(`/admin/applicants/${a.id}`)}
                      className="cursor-pointer border-b border-blush-100 transition-colors last:border-none hover:bg-blush-50/70">
                      <td className="px-6 py-4">
                        <p className="font-semibold text-charcoal">{a.legal_name}</p>
                        <p className="text-xs text-charcoal-faint">{a.email}</p>
                        <p className="text-xs text-charcoal-faint">{a.phone}</p>
                      </td>
                      <td className="px-4 py-4 text-charcoal-light">{a.positions.join(", ") || "—"}</td>
                      <td className="px-4 py-4 text-charcoal-light">{[...(a.availability?.types || []), ...(a.availability?.shifts || [])].slice(0, 3).join(", ") || "—"}</td>
                      <td className="max-w-[160px] truncate px-4 py-4 text-charcoal-light">{a.service_areas || "—"}</td>
                      <td className="whitespace-nowrap px-4 py-4 text-charcoal-muted">
                        {a.created_at ? new Date(a.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                      </td>
                      <td className="px-4 py-4"><StatusBadge status={a.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
